import { AxiosResponse } from "axios";
import { call, fork, put, select, takeLatest } from "redux-saga/effects";

import {
  CreateReportPayload,
  ExportFormat,
  REPORT_FILE_EXTENSIONS,
  REPORT_TOASTS,
  ReportItem,
  ReportListResponse,
  statusTransitions,
} from "entities/reports";
import { ReportsApi } from "services/http";
import { deliverFile } from "utils/deliverFile";

import { RootStore } from "store";
import { hideModal } from "store/slices/modalSlice";
import {
  createReportFailure,
  createReportRequest,
  createReportSuccess,
  downloadReportRequest,
  getReportListFailure,
  getReportListRequest,
  getReportListSuccess,
  removeReportFailure,
  removeReportRequest,
  removeReportSuccess,
  retryReportRequest,
} from "store/slices/reportsSlice";
import { showToast } from "store/slices/toastSlice";

function* refreshList() {
  const lastQuery: string = yield select((state: RootStore) => state.reports.lastQuery);
  yield put(getReportListRequest(lastQuery));
}

function* getListFlow(action: { type: string; payload: string }) {
  try {
    const previous: ReportItem[] = yield select((state: RootStore) => state.reports.list);
    const response: AxiosResponse<ReportListResponse> = yield call(ReportsApi.getList, action.payload);
    yield put(getReportListSuccess(response.data));

    for (const transition of statusTransitions(previous, response.data.items)) {
      yield put(
        showToast({
          id: `report-${transition.id}-${transition.status}`,
          message:
            transition.status === "ready" ? REPORT_TOASTS.GENERATION_COMPLETED : REPORT_TOASTS.GENERATION_FAILED,
          type: transition.status === "ready" ? "success" : "error",
        }),
      );
    }
  } catch {
    yield put(getReportListFailure());
  }
}

function* createFlow(action: { type: string; payload: CreateReportPayload }) {
  try {
    yield call(ReportsApi.create, action.payload);
    yield put(createReportSuccess());
    yield put(hideModal());
    yield put(
      showToast({
        id: "report-generation-started",
        message: REPORT_TOASTS.GENERATION_STARTED,
        type: "success",
      }),
    );
    yield* refreshList();
  } catch {
    yield put(createReportFailure());
    yield put(
      showToast({
        id: "report-generation-start-failed",
        message: REPORT_TOASTS.GENERATION_START_FAILED,
        type: "error",
      }),
    );
  }
}

function* downloadFlow(action: { type: string; payload: { id: number; name: string; format: ExportFormat } }) {
  try {
    const response: AxiosResponse<Blob> = yield call(ReportsApi.download, action.payload.id);
    const url = URL.createObjectURL(response.data);
    const filename = `${action.payload.name}.${REPORT_FILE_EXTENSIONS[action.payload.format]}`;

    deliverFile({ url, filename, openInTab: action.payload.format === "pdf" });

    setTimeout(() => URL.revokeObjectURL(url), 60000);
    yield* refreshList();
  } catch {
    yield put(
      showToast({
        id: `report-download-failed-${action.payload.id}`,
        message: REPORT_TOASTS.DOWNLOAD_FAILED,
        type: "error",
      }),
    );
  }
}

function* removeFlow(action: { type: string; payload: number }) {
  try {
    yield call(ReportsApi.remove, action.payload);
    yield put(removeReportSuccess());
    yield put(hideModal());
    yield put(showToast({ id: "report-deleted", message: REPORT_TOASTS.DELETED, type: "success" }));
    yield* refreshList();
  } catch {
    yield put(removeReportFailure());
    yield put(showToast({ id: "report-delete-failed", message: REPORT_TOASTS.DELETE_FAILED, type: "error" }));
  }
}

function* retryFlow(action: { type: string; payload: number }) {
  try {
    yield call(ReportsApi.retry, action.payload);
    yield* refreshList();
  } catch {
    yield put(
      showToast({
        id: `report-retry-failed-${action.payload}`,
        message: REPORT_TOASTS.GENERATION_START_FAILED,
        type: "error",
      }),
    );
  }
}

function* watchGetList() {
  yield takeLatest(getReportListRequest.type, getListFlow);
}

function* watchCreate() {
  yield takeLatest(createReportRequest.type, createFlow);
}

function* watchDownload() {
  yield takeLatest(downloadReportRequest.type, downloadFlow);
}

function* watchRemove() {
  yield takeLatest(removeReportRequest.type, removeFlow);
}

function* watchRetry() {
  yield takeLatest(retryReportRequest.type, retryFlow);
}

export const reportWatchers = [
  fork(watchGetList),
  fork(watchCreate),
  fork(watchDownload),
  fork(watchRemove),
  fork(watchRetry),
];
